import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useQuery } from '@tanstack/react-query';
import { VendorService } from '@/services/vendor.service';
import { VendorType, VendorStatus } from '@/models/vendor.model';

export const Vendors: React.FC = () => {
  const { profile } = useAuth();
  const [activeTab, setActiveTab] = useState<'vendors' | 'purchaseOrders' | 'invoices'>('vendors');
  const [typeFilter, setTypeFilter] = useState<'all' | VendorType>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | VendorStatus>('all');
  const [search, setSearch] = useState('');

  const { data: vendors = [], isLoading: vendorsLoading } = useQuery({
    queryKey: ['vendors', typeFilter, statusFilter],
    queryFn: async () => {
      const all = await VendorService.getAllVendors();
      let filtered = all;
      if (typeFilter !== 'all') filtered = filtered.filter(v => v.type === typeFilter);
      if (statusFilter !== 'all') filtered = filtered.filter(v => v.status === statusFilter);
      if (search.trim()) {
        const s = search.trim().toLowerCase();
        filtered = filtered.filter(v =>
          v.name.toLowerCase().includes(s) ||
          (v.email || '').toLowerCase().includes(s) ||
          (v.contactName || '').toLowerCase().includes(s) ||
          (v.tags || []).some(t => t.toLowerCase().includes(s))
        );
      }
      return filtered;
    },
    enabled: !!profile,
  });

  const { data: purchaseOrders = [] } = useQuery({
    queryKey: ['purchaseOrders'],
    queryFn: () => VendorService.getAllPurchaseOrders(),
    enabled: !!profile,
  });

  const { data: invoices = [] } = useQuery({
    queryKey: ['vendorInvoices'],
    queryFn: () => VendorService.getAllInvoices(),
    enabled: !!profile,
  });

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      active: 'bg-green-100 text-green-800',
      inactive: 'bg-gray-100 text-gray-800',
      pending: 'bg-yellow-100 text-yellow-800',
      suspended: 'bg-red-100 text-red-800',
      draft: 'bg-gray-100 text-gray-800',
      approved: 'bg-blue-100 text-blue-800',
      sent: 'bg-indigo-100 text-indigo-800',
      received: 'bg-green-100 text-green-800',
      cancelled: 'bg-gray-100 text-gray-800',
      paid: 'bg-green-100 text-green-800',
      overdue: 'bg-red-100 text-red-800',
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const activeVendors = vendors.filter(v => v.status === VendorStatus.active).length;
  const totalSpent = invoices.filter(i => i.status === 'paid').reduce((sum, i) => sum + i.total, 0);
  const pendingInvoices = invoices.filter(i => i.status === 'pending' || i.status === 'overdue').length;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Vendor & Procurement</h1>
        <p className="mt-2 text-gray-600">Manage vendors, purchase orders, and invoices</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-600">Total Vendors</div>
          <div className="text-2xl font-bold text-gray-900">{vendors.length}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-600">Active Vendors</div>
          <div className="text-2xl font-bold text-green-600">{activeVendors}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-600">Total Spent</div>
          <div className="text-2xl font-bold text-blue-600">${totalSpent.toLocaleString()}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-600">Pending Invoices</div>
          <div className="text-2xl font-bold text-orange-600">{pendingInvoices}</div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          {[
            { id: 'vendors' as const, name: 'Vendors', count: vendors.length },
            { id: 'purchaseOrders' as const, name: 'Purchase Orders', count: purchaseOrders.length },
            { id: 'invoices' as const, name: 'Invoices', count: invoices.length },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.name} {tab.count > 0 && <span className="ml-2 bg-gray-100 text-gray-900 py-0.5 px-2.5 rounded-full text-xs">{tab.count}</span>}
            </button>
          ))}
        </nav>
      </div>

      {/* Vendors Tab */}
      {activeTab === 'vendors' && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <div className="flex flex-wrap gap-4">
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search vendors..."
                className="border border-gray-300 rounded-md shadow-sm py-2 px-3 text-sm w-64"
              />
              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value as any)}
                className="border border-gray-300 rounded-md shadow-sm py-2 px-3 text-sm"
              >
                <option value="all">All Types</option>
                {Object.values(VendorType).map(type => (
                  <option key={type} value={type}>{type.replace('_', ' ')}</option>
                ))}
              </select>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as any)}
                className="border border-gray-300 rounded-md shadow-sm py-2 px-3 text-sm"
              >
                <option value="all">All Statuses</option>
                {Object.values(VendorStatus).map(status => (
                  <option key={status} value={status}>{status}</option>
                ))}
              </select>
            </div>
            <Link
              to="/vendors/new"
              className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 text-sm font-medium"
            >
              + Add Vendor
            </Link>
          </div>

          {vendorsLoading ? (
            <div className="text-center py-8">Loading...</div>
          ) : vendors.length === 0 ? (
            <div className="bg-white rounded-lg shadow p-8 text-center">
              <p className="text-gray-600">No vendors found</p>
            </div>
          ) : (
            <div className="bg-white shadow overflow-hidden sm:rounded-md">
              <ul className="divide-y divide-gray-200">
                {vendors.map((vendor) => (
                  <li key={vendor.id}>
                    <Link
                      to={`/vendors/${vendor.id}`}
                      className="block hover:bg-gray-50 px-6 py-4"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center">
                            <p className="text-sm font-medium text-gray-900">{vendor.name}</p>
                            <span className={`ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(vendor.status)}`}>
                              {vendor.status}
                            </span>
                            <span className="ml-2 text-xs text-gray-500">{vendor.type.replace('_', ' ')}</span>
                          </div>
                          <p className="mt-1 text-sm text-gray-500">
                            {vendor.email} {vendor.phone && `• ${vendor.phone}`}
                            {vendor.paymentTerms && `• Terms: ${vendor.paymentTerms}`}
                          </p>
                        </div>
                        <div className="text-right">
                          {vendor.rating && (
                            <p className="text-sm text-gray-900">⭐ {vendor.rating}/5</p>
                          )}
                        </div>
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Purchase Orders Tab */}
      {activeTab === 'purchaseOrders' && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold">Purchase Orders</h2>
            <Link
              to="/vendors/purchase-orders/new"
              className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 text-sm font-medium"
            >
              + Create PO
            </Link>
          </div>
          {purchaseOrders.length === 0 ? (
            <div className="bg-white rounded-lg shadow p-8 text-center">
              <p className="text-gray-600">No purchase orders found</p>
            </div>
          ) : (
            <div className="bg-white shadow overflow-hidden sm:rounded-md">
              <ul className="divide-y divide-gray-200">
                {purchaseOrders.map((po) => (
                  <li key={po.id}>
                    <Link to={`/vendors/purchase-orders/${po.id}`} className="block hover:bg-gray-50 px-6 py-4">
                      <div className="flex items-center justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <p className="text-sm font-medium text-gray-900">{po.poNumber || po.id}</p>
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(po.status)}`}>
                              {po.status}
                            </span>
                          </div>
                          <p className="mt-1 text-sm text-gray-500">
                            {po.vendorName} • {po.orderDate.toLocaleDateString()}
                            {po.expectedDeliveryDate ? ` • Expected ${po.expectedDeliveryDate.toLocaleDateString()}` : ''}
                          </p>
                        </div>
                        <div className="text-right text-sm text-gray-900">${po.total.toLocaleString()}</div>
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Invoices Tab */}
      {activeTab === 'invoices' && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold">Invoices</h2>
            <Link
              to="/vendors/invoices/new"
              className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 text-sm font-medium"
            >
              + Add Invoice
            </Link>
          </div>
          {invoices.length === 0 ? (
            <div className="bg-white rounded-lg shadow p-8 text-center">
              <p className="text-gray-600">No invoices found</p>
            </div>
          ) : (
            <div className="bg-white shadow overflow-hidden sm:rounded-md">
              <ul className="divide-y divide-gray-200">
                {invoices.map((inv) => (
                  <li key={inv.id}>
                    <Link to={`/vendors/invoices/${inv.id}`} className="block hover:bg-gray-50 px-6 py-4">
                      <div className="flex items-center justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <p className="text-sm font-medium text-gray-900">{inv.invoiceNumber || inv.id}</p>
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(inv.status)}`}>
                              {inv.status}
                            </span>
                          </div>
                          <p className="mt-1 text-sm text-gray-500">
                            {inv.vendorName} • {inv.invoiceDate.toLocaleDateString()} • Due {inv.dueDate.toLocaleDateString()}
                          </p>
                        </div>
                        <div className="text-right text-sm text-gray-900">${inv.total.toLocaleString()}</div>
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

